import tw from 'twin.macro'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { CgCalendar, CgImage, CgTrash } from 'react-icons/cg'

import { useDialog } from 'common/components/Dialog/hooks/useDialog'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { useCustomization } from 'common/contexts/Customization/useCustomization'
import { useFormRefsControl } from 'common/components/RefsControl/FormRefsControl/useFormRefsControl'
import { useEventListener } from 'common/hooks/useEventListener'
import { useListenExercisesQuery } from 'modules/ExerciseLibrary/exerciseLibraryApi'
import { useListenCoachVideosQuery } from 'modules/VideoLibrary/videoLibraryApi'
import {
  useListenProgramGroupsQuery,
  useListenProgramMultiWktDaysQuery,
  useListenProgramWorkoutsQuery,
} from 'modules/Layout/layoutApi'
import { useListenCollectionsQuery } from 'modules/Collections/collectionsApi'
import { useListenOrgProgramsListQuery } from 'modules/Programs/programApi'

import { Switch } from 'common/components/Switch/Switch'
import { Input } from 'common/components/Input/Input'
import { Textarea } from 'common/components/Textarea/Textarea'
import { UpdateActions } from 'common/components/UpdateActions/UpdateActions'
import { createUID } from 'common/utils/createUID'

import { useCreateCommunityPostMutation, useEditCommunityPostMutation } from '../communityApi'
import { isPostPinned } from '../communityUtils'
import useCoachAuthor from '../useCoachAuthor'
import { POST_MAX_LENGTH, PIN_DURATION_MS } from '../communityConstants'
import { getLandOrPortExThumb } from 'common/utils/exerciseUtils'
import { isVideoReady, VIDEO_PROCESSING_LABEL, VIDEO_PROCESSING_MESSAGE } from 'common/utils/videoUtils'
import {
  buildButtonFields,
  EXERCISE_UNAVAILABLE_MESSAGE,
  getAttachableExercises,
  getAttachableNamedItems,
  isUnattachableExercise,
  getBorrowedThumbnail,
  stripNullFields,
  GROUP_CALL_TYPE,
} from '../postButtonUtils'
import { getDeviceTimeZone, isValidTimeZone, utcMsToDatePickerDate, zonedDateTimeToUtcMs } from '../timeZoneUtils'
import FittedThumb from '../components/FittedThumb'
import { uploadPostImage } from '../postImageUpload'
import ButtonTypeInput from './ButtonTypeInput'
import SceneInput, { availableScenes } from './SceneInput'
import SuggestionInput from './SuggestionInput'
import TimeZoneInput from './TimeZoneInput'
import { schema } from './schema'
import {
  getAvailableProgramWorkouts,
  getProgramWorkoutsLoading,
  getWorkoutDayLabel,
  shouldClearWorkoutId,
} from './workoutOptions'

const ONE_HOUR_MS = 60 * 60 * 1000

export default function CommunityPostForm({ post, coachOrgId, userId }) {
  const [, setDialogOpen] = useDialog()
  const { createAlert } = useAlert()

  const [loading, setLoading] = useState(false)
  const isNewPost = !Boolean(post)
  const wasPinned = isPostPinned(post, Date.now())

  const [createCommunityPost] = useCreateCommunityPostMutation()
  const [editCommunityPost] = useEditCommunityPostMutation()

  const author = useCoachAuthor({ coachOrgId, userId })

  // A brand new group call defaults to the next full hour in the coach's own
  // timezone, running for an hour.
  const nowTime = useRef(Date.now())
  const defaultCallTimeZone = post?.callTimeZone || getDeviceTimeZone()
  const defaultCallStartAt = post?.callStartAt || getDefaultCallStartAt(defaultCallTimeZone, nowTime.current)
  const defaultCallEndAt = post?.callEndAt || defaultCallStartAt + ONE_HOUR_MS

  const defaultValues = {
    text: post?.text || '',
    buttonType: post?.linkType || 'none',
    link: post?.link || '',
    openInDefaultBrowser: post?.openInDefaultBrowser || false,
    scene: post?.scene || '',
    videoId: post?.videoId || '',
    programId: post?.programId || '',
    workoutId: post?.workoutId || '',
    exerciseId: post?.exerciseId || '',
    collectionId: post?.collectionId || '',
    programGroupId: post?.programGroupId || '',
    callStartAt: defaultCallStartAt,
    callEndAt: defaultCallEndAt,
    callTimeZone: defaultCallTimeZone,
    notifyMembers: isNewPost,
    pinned: wasPinned,
    repliesOff: post?.repliesOff === true,
  }

  const {
    watch,
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    setError,
    clearErrors,
  } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  })

  const formState = watch()

  // Photo: a locally-picked file, the post's existing photo, or nothing.
  const [imageFile, setImageFile] = useState(null)
  const [imageRemoved, setImageRemoved] = useState(false)
  const fileInputRef = useRef()

  const { data: videoData } = useListenCoachVideosQuery({ coachOrgId })
  const videos = videoData?.isLoading ? {} : videoData || {}
  const { data: programsData } = useListenOrgProgramsListQuery({ orgId: coachOrgId })
  const programs = programsData?.stillLoading ? {} : programsData || {}
  const { data: programWorkoutsData } = useListenProgramWorkoutsQuery(
    { programId: formState?.programId },
    { skip: formState?.buttonType !== 'workout' || !formState?.programId }
  )
  const programWorkoutsLoading = getProgramWorkoutsLoading({
    isWorkoutButton: formState?.buttonType === 'workout',
    programId: formState?.programId,
    programWorkoutsData,
  })
  const { data: multiWktDaysData } = useListenProgramMultiWktDaysQuery(
    { programId: formState?.programId },
    { skip: formState?.buttonType !== 'workout' || !formState?.programId }
  )
  const programWorkouts = useMemo(
    () => getAvailableProgramWorkouts(programWorkoutsData, multiWktDaysData),
    [programWorkoutsData, multiWktDaysData]
  )
  const { exVidOrientation } = useCustomization()
  const { data: exercisesData } = useListenExercisesQuery(
    { coachOrgId, exVidOrientation },
    { skip: formState?.buttonType !== 'exercise' }
  )
  const exercises = useMemo(() => getAttachableExercises(exercisesData), [exercisesData])
  // Also loaded for a post saved with one, to tell whether its photo was borrowed.
  const { data: collectionsData } = useListenCollectionsQuery(
    { coachOrgId },
    { skip: formState?.buttonType !== 'collection' && post?.linkType !== 'collection' }
  )
  const collections = useMemo(() => getAttachableNamedItems(collectionsData), [collectionsData])
  const { data: programGroupsData } = useListenProgramGroupsQuery(
    { coachOrgId },
    { skip: formState?.buttonType !== 'programGroup' && post?.linkType !== 'programGroup' }
  )
  const programGroups = useMemo(() => getAttachableNamedItems(programGroupsData), [programGroupsData])
  // Each list counts as loading until its query answers; `null` is an answer (nothing there).
  const videosLoading = videoData === undefined || videoData?.isLoading === true
  const programsLoading = programsData === undefined || programsData?.stillLoading === true
  const workoutsLoading =
    programWorkoutsLoading ||
    (Boolean(formState?.programId) && (multiWktDaysData === undefined || multiWktDaysData?.isLoading === true))
  const exercisesLoading = exercisesData === undefined || exercisesData?.isFiller === true
  const collectionsLoading = collectionsData === undefined || collectionsData?.isLoading === true
  const programGroupsLoading = programGroupsData === undefined || programGroupsData?.isLoading === true

  const selectedVideoLabel = videos?.[formState?.videoId]?.title || ''
  const selectedProgramLabel = programs?.[formState?.programId]?.name || ''
  const selectedWorkoutLabel = programWorkouts?.[formState?.workoutId]?.title || ''
  const selectedExerciseLabel = exercises?.[formState?.exerciseId]?.name || ''
  const selectedCollectionLabel = collections?.[formState?.collectionId]?.name || ''
  const selectedProgramGroupLabel = programGroups?.[formState?.programGroupId]?.name || ''

  const savedBorrowedThumbnail = getBorrowedThumbnail({
    buttonType: post?.linkType,
    scene: post?.scene,
    videoId: post?.videoId,
    programId: post?.programId,
    workoutId: post?.workoutId,
    exerciseId: post?.exerciseId,
    collectionId: post?.collectionId,
    programGroupId: post?.programGroupId,
    videos,
    programs,
    workouts: programWorkouts,
    exercises,
    collections,
    programGroups,
    exVidOrientation,
    scenes: availableScenes,
  })
  const [savedImageIsBorrowed, setSavedImageIsBorrowed] = useState(false)
  useEffect(() => {
    if (post?.imageUrl && post.imageUrl === savedBorrowedThumbnail) setSavedImageIsBorrowed(true)
  }, [post?.imageUrl, savedBorrowedThumbnail])
  const savedPhotoUrl = savedImageIsBorrowed ? null : post?.imageUrl || null

  const imagePreviewUrl = useMemo(() => {
    if (imageFile) return URL.createObjectURL(imageFile)
    if (!imageRemoved && savedPhotoUrl) return savedPhotoUrl
    return null
  }, [imageFile, imageRemoved, savedPhotoUrl])
  useEffect(() => {
    return () => {
      if (imageFile && imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl)
    }
  }, [imageFile, imagePreviewUrl])

  // A photo on a post with a button is that button's thumbnail, not a photo of
  // its own — the form's photo field says so.
  const hasButton = formState.buttonType !== 'none'
  const isGroupCall = formState.buttonType === GROUP_CALL_TYPE

  // A group call gets a second, separate push when the call starts, sent by the
  // cron and not governed by this toggle. Say so here, or turning the toggle
  // off and then seeing that reminder arrive looks like the switch is broken.
  const notifyMembersTooltip = [
    'Sends a push notification about this post to all active paid members',
    ...(isGroupCall ? ['Reminders are always sent to members when the call starts'] : []),
  ].join('. ')

  const callStartRef = useRef()
  const callEndRef = useRef()
  const callTimeZoneRef = useRef()

  const { addManyInputRefs, clearControlledRefs, moveFocusOnKeyPress } = useFormRefsControl()
  const textRef = useRef()
  const buttonTypeRef = useRef()
  const sceneRef = useRef()
  const videoRef = useRef()
  const programRef = useRef()
  const workoutRef = useRef()
  const exerciseRef = useRef()
  const collectionRef = useRef()
  const programGroupRef = useRef()
  const linkRef = useRef()
  const submitRef = useRef()

  useEffect(() => {
    const buttonRefs = {
      scene: [{ ref: sceneRef, name: 'scene' }],
      video: [{ ref: videoRef, name: 'video' }],
      program: [{ ref: programRef, name: 'program' }],
      workout: [
        { ref: programRef, name: 'program' },
        { ref: workoutRef, name: 'workout' },
      ],
      exercise: [{ ref: exerciseRef, name: 'exercise' }],
      collection: [{ ref: collectionRef, name: 'collection' }],
      programGroup: [{ ref: programGroupRef, name: 'programGroup' }],
      link: [{ ref: linkRef, name: 'link' }],
      [GROUP_CALL_TYPE]: [
        { ref: linkRef, name: 'link' },
        { ref: { current: callStartRef?.current?.input }, name: 'callStartAt' },
        { ref: { current: callEndRef?.current?.input }, name: 'callEndAt' },
        { ref: callTimeZoneRef, name: 'callTimeZone' },
      ],
    }

    clearControlledRefs()
    addManyInputRefs([
      { ref: textRef, name: 'text' },
      { ref: buttonTypeRef, name: 'buttonType' },
      ...(buttonRefs[formState.buttonType] || []),
      { ref: submitRef, name: 'submit' },
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.buttonType])

  // The date pickers hand back a plain JS Date holding the wall-clock time the
  // coach picked ("6:00 PM on Mar 4"). Which actual moment that is depends on the
  // chosen timezone, so convert through it before storing.
  const setGroupCallDateValue = (name, date, timeZone = formState.callTimeZone) => {
    if (!date) return

    try {
      setValue(name, zonedDateTimeToUtcMs(date, timeZone), { shouldValidate: true })
      clearErrors(name)
    } catch (error) {
      setError(name, { type: 'manual', message: error.message })
    }
  }

  // Switching timezone keeps the wall-clock times the coach already picked and
  // re-anchors them to the new zone (6pm stays 6pm, the moment moves).
  const handleGroupCallTimeZoneChange = (timeZone) => {
    const currentStartDate = utcMsToDatePickerDate(formState.callStartAt, formState.callTimeZone)
    const currentEndDate = utcMsToDatePickerDate(formState.callEndAt, formState.callTimeZone)

    setValue('callTimeZone', timeZone, { shouldValidate: true })
    clearErrors('callTimeZone')

    if (isValidTimeZone(timeZone)) {
      ;['callStartAt', 'callEndAt'].forEach((name) => {
        if (errors?.[name]?.message === 'Invalid timezone') clearErrors(name)
      })
    }

    setGroupCallDateValue('callStartAt', currentStartDate, timeZone)
    setGroupCallDateValue('callEndAt', currentEndDate, timeZone)
  }

  useEffect(() => {
    if (
      shouldClearWorkoutId({
        buttonType: formState.buttonType,
        programId: formState.programId,
        workoutId: formState.workoutId,
        programWorkouts,
        programWorkoutsLoading,
      })
    ) {
      setValue('workoutId', '')
    }
  }, [
    formState.buttonType,
    formState.programId,
    formState.workoutId,
    programWorkouts,
    programWorkoutsLoading,
    setValue,
  ])

  const handlePickImage = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setImageFile(file)
      setImageRemoved(false)
    }
    e.target.value = ''
  }

  const handleRemoveImage = () => {
    setImageFile(null)
    setImageRemoved(true)
  }

  const onSubmit = async (data) => {
    // The picker will not offer a processing video, but a post saved earlier can
    // still point at one. Posting it now would give members a card that cannot play
    // and, for a new post, a push notification about it, so hold the post until the
    // video is playable. A video missing from the library altogether is left alone
    // here: the library may simply not have loaded yet.
    const selectedVideo = data.buttonType === 'video' ? videos?.[data.videoId] : null
    if (selectedVideo && !isVideoReady(selectedVideo)) {
      setError('videoId', { type: 'manual', message: VIDEO_PROCESSING_MESSAGE })
      return
    }
    if (data.buttonType === 'exercise' && isUnattachableExercise(exercisesData, data.exerciseId)) {
      setError('exerciseId', { type: 'manual', message: EXERCISE_UNAVAILABLE_MESSAGE })
      return
    }

    setLoading(true)
    const postId = post?.id || createUID()

    // Resolve the post image: uploaded photo wins; otherwise keep the existing one;
    // otherwise borrow the linked content's thumbnail (announcements behavior).
    let imageUrl = null
    if (imageFile) {
      const uploadResult = await uploadPostImage({ coachOrgId, userId, postId, file: imageFile })
      if (uploadResult.error) {
        setLoading(false)
        createAlert({ text: 'Image upload failed. Please try again.', type: 'danger' })
        return
      }
      imageUrl = uploadResult.imageUrl
    } else if (!imageRemoved && savedPhotoUrl) {
      imageUrl = savedPhotoUrl
    }
    if (!imageUrl) {
      imageUrl = getBorrowedThumbnail({
        buttonType: data.buttonType,
        scene: data.scene,
        videoId: data.videoId,
        programId: data.programId,
        workoutId: data.workoutId,
        exerciseId: data.exerciseId,
        collectionId: data.collectionId,
        programGroupId: data.programGroupId,
        videos,
        programs,
        workouts: programWorkouts,
        exercises,
        collections,
        programGroups,
        exVidOrientation,
        scenes: availableScenes,
      })
    }

    const buttonFields = buildButtonFields(data)

    let error
    if (isNewPost) {
      const result = await createCommunityPost({
        coachOrgId,
        userId,
        author,
        text: data.text,
        notifyMembers: data.notifyMembers,
        pinned: data.pinned,
        repliesOff: data.repliesOff,
        postId,
        richFields: stripNullFields({ ...buttonFields, imageUrl }),
      })
      error = result?.error || result?.data?.error
    } else {
      const fields = {
        text: data.text,
        imageUrl: imageUrl || null,
        ...buttonFields,
      }
      // Pin/replies changes ride along only when actually toggled, so an unrelated
      // text edit doesn't restart the 30-day pin window.
      if (data.pinned !== wasPinned) {
        fields.pinned = data.pinned ? true : null
        fields.pinnedUntil = data.pinned ? Date.now() + PIN_DURATION_MS : null
      }
      if (data.repliesOff !== (post?.repliesOff === true)) {
        fields.repliesOff = data.repliesOff ? true : null
      }
      // Keep the cron's "starting now" push queue in sync: a group call
      // (re)queues at its current start time; a button changed away from
      // group call clears the queued push.
      let groupCallQueueUpdate
      if (data.buttonType === GROUP_CALL_TYPE && typeof buttonFields.callStartAt === 'number') {
        groupCallQueueUpdate = { callStartAt: buttonFields.callStartAt }
      } else if (post?.linkType === GROUP_CALL_TYPE) {
        groupCallQueueUpdate = { remove: true }
      }
      const result = await editCommunityPost({ coachOrgId, postId, fields, groupCallQueueUpdate })
      error = result?.error || result?.data?.error
    }

    setLoading(false)
    if (error) {
      createAlert({ text: `Could not ${isNewPost ? 'create' : 'save'} post. Please try again.`, type: 'danger' })
      return
    }
    createAlert({ text: isNewPost ? 'Post created!' : 'Post updated!', type: 'success' })
    setDialogOpen(false)
  }

  useEventListener('keydown', (e) => moveFocusOnKeyPress(e, handleSubmit(onSubmit)))

  const textLength = formState?.text?.length || 0

  return (
    <>
      <div className='divide-y divide-gray-200 overflow-auto'>
        <form onSubmit={handleSubmit(onSubmit)} id='communityPostForm'>
          <div className='flex flex-col px-10 py-4'>
            <div className='mb-1 flex items-end justify-between'>
              <label htmlFor='text' className='inline-flex cursor-pointer font-semibold text-tBlack'>
                Post
              </label>
              <span className='text-xs text-gray-500'>
                {textLength}/{POST_MAX_LENGTH}
              </span>
            </div>
            <div className='mb-4'>
              <Textarea
                name='text'
                placeholder="What's on your mind?"
                register={register}
                inputRef={textRef}
                error={errors?.text?.message}
                maxLength={POST_MAX_LENGTH}
                rows={5}
              />
              {errors?.text && <p className='flex text-xs mt-1 text-tRed'>{errors.text.message}</p>}
            </div>

            <ButtonTypeInput
              inputRef={buttonTypeRef}
              value={formState.buttonType}
              setValue={setValue}
              clearErrors={clearErrors}
            />
            {formState.buttonType === 'scene' && (
              <SceneInput
                inputRef={sceneRef}
                value={formState.scene}
                setValue={setValue}
                coachOrgId={coachOrgId}
                error={errors?.scene}
              />
            )}
            {formState.buttonType === 'video' && (
              <SuggestionInput
                inputRef={videoRef}
                label='Video'
                name='videoId'
                value={formState?.videoId}
                selectedLabel={selectedVideoLabel}
                suggestions={videos}
                setValue={setValue}
                clearErrors={clearErrors}
                error={errors?.videoId}
                getItemLabel={(video) => video?.title || ''}
                getPreviewImg={(video) => video?.previewImg}
                isItemDisabled={(video) => !isVideoReady(video)}
                disabledLabel={VIDEO_PROCESSING_LABEL}
                isLoading={videosLoading}
              />
            )}
            {(formState.buttonType === 'program' || formState.buttonType === 'workout') && (
              <SuggestionInput
                inputRef={programRef}
                label='Program'
                name='programId'
                value={formState?.programId}
                selectedLabel={selectedProgramLabel}
                suggestions={programs}
                setValue={setValue}
                clearErrors={clearErrors}
                error={errors?.programId}
                getItemLabel={(program) => program?.name || ''}
                getPreviewImg={(program) => program?.previewImg}
                listAllOnFocus
                onSelect={() => {
                  if (formState.buttonType === 'workout') {
                    setValue('workoutId', '', { shouldValidate: true })
                  }
                }}
                isLoading={programsLoading}
              />
            )}
            {formState.buttonType === 'workout' && (
              <SuggestionInput
                inputRef={workoutRef}
                label='Workout'
                name='workoutId'
                value={formState?.workoutId}
                selectedLabel={selectedWorkoutLabel}
                suggestions={programWorkouts}
                setValue={setValue}
                clearErrors={clearErrors}
                error={errors?.workoutId}
                getItemLabel={(workout) => workout?.title || ''}
                getPreviewImg={(workout) => workout?.previewImg}
                getItemSubtitle={getWorkoutDayLabel}
                listAllOnFocus
                isLoading={workoutsLoading}
              />
            )}
            {formState.buttonType === 'exercise' && (
              <SuggestionInput
                inputRef={exerciseRef}
                label='Exercise'
                name='exerciseId'
                value={formState?.exerciseId}
                selectedLabel={selectedExerciseLabel}
                suggestions={exercises}
                setValue={setValue}
                clearErrors={clearErrors}
                error={errors?.exerciseId}
                getItemLabel={(exercise) => exercise?.name || ''}
                getPreviewImg={(exercise) => getLandOrPortExThumb(exVidOrientation, exercise)}
                isLoading={exercisesLoading}
              />
            )}
            {formState.buttonType === 'collection' && (
              <SuggestionInput
                inputRef={collectionRef}
                label='Collection'
                name='collectionId'
                value={formState?.collectionId}
                selectedLabel={selectedCollectionLabel}
                suggestions={collections}
                setValue={setValue}
                clearErrors={clearErrors}
                error={errors?.collectionId}
                getItemLabel={(collection) => collection?.name || ''}
                getPreviewImg={(collection) => collection?.previewImg}
                listAllOnFocus
                isLoading={collectionsLoading}
              />
            )}
            {formState.buttonType === 'programGroup' && (
              <SuggestionInput
                inputRef={programGroupRef}
                label='Series'
                name='programGroupId'
                value={formState?.programGroupId}
                selectedLabel={selectedProgramGroupLabel}
                suggestions={programGroups}
                setValue={setValue}
                clearErrors={clearErrors}
                error={errors?.programGroupId}
                getItemLabel={(programGroup) => programGroup?.name || ''}
                getPreviewImg={(programGroup) => programGroup?.previewImg}
                placeholder='Search series'
                listAllOnFocus
                isLoading={programGroupsLoading}
              />
            )}
            {(formState.buttonType === 'link' || isGroupCall) && (
              <div className='mb-4'>
                <label htmlFor='link' className='inline-flex cursor-pointer font-semibold text-tBlack mb-1'>
                  {isGroupCall ? 'Group call link' : 'Link'}
                </label>
                <Input
                  name='link'
                  placeholder={isGroupCall ? 'Livestream on YouTube, Zoom, etc.' : 'https://...'}
                  register={register}
                  inputRef={linkRef}
                  error={errors?.link?.message}
                />
                {errors?.link && <p className='flex text-xs mt-1 text-tRed'>{errors.link.message}</p>}
              </div>
            )}
            {/* Group call links always open outside the app (Zoom, Meet, Teams all
                deep-link into their own app), so there is no in-app choice here. */}
            {formState.buttonType === 'link' && (
              <div className='mb-4'>
                <Switch
                  name='openInDefaultBrowser'
                  onChange={({ target: { checked } }) => setValue('openInDefaultBrowser', !checked)}
                  label='Link opens in-app'
                  isChecked={!formState.openInDefaultBrowser}
                  tooltipText='If your link opens another app, ie. YouTube, Instagram, Telegram, etc. disable this for best user experience'
                />
              </div>
            )}
            {isGroupCall && (
              <>
                <div className='grid grid-cols-2 gap-4 mb-4'>
                  <div className='flex flex-col'>
                    <label
                      onClick={() => callStartRef?.current?.setOpen(true)}
                      htmlFor='callStartAt'
                      className='inline-flex cursor-pointer font-semibold text-tBlack self-start mb-1'
                    >
                      Call start
                    </label>
                    <DatePicker
                      name='callStartAt'
                      ref={callStartRef}
                      selected={utcMsToDatePickerDate(formState.callStartAt, formState.callTimeZone)}
                      onChange={(date) => setGroupCallDateValue('callStartAt', date)}
                      placeholderText='mm/dd/yyyy h:mm aa'
                      css={[dateTimeInputClasses, errors.callStartAt && tw`ring-tRed focus:ring-tRed`]}
                      showIcon={true}
                      showTimeSelect={true}
                      timeIntervals={15}
                      dateFormat='MM/dd/yyyy h:mm aa'
                      icon={
                        <CgCalendar
                          className='!w-5 !h-5 top-1/2 -translate-y-1/2'
                          onClick={() => callStartRef?.current?.setOpen(true)}
                        />
                      }
                      popperProps={{ placement: 'bottom-start', strategy: 'fixed' }}
                      // The date picker swallows Tab before it reaches the form's
                      // window-level key handler, so close the calendar and move
                      // focus on from here instead.
                      onKeyDown={(e) => {
                        if (e.code === 'Tab') {
                          e.preventDefault()
                          e.stopPropagation()
                          callStartRef?.current?.setOpen(false)
                          moveFocusOnKeyPress(e)
                        }
                      }}
                      onCalendarOpen={() => callEndRef?.current?.setOpen(false)}
                    />
                    {errors?.callStartAt && <p className='flex text-xs mt-1 text-tRed'>{errors.callStartAt.message}</p>}
                  </div>
                  <div className='flex flex-col'>
                    <label
                      onClick={() => callEndRef?.current?.setOpen(true)}
                      htmlFor='callEndAt'
                      className='inline-flex cursor-pointer font-semibold text-tBlack self-start mb-1'
                    >
                      Call end
                    </label>
                    <DatePicker
                      name='callEndAt'
                      ref={callEndRef}
                      selected={utcMsToDatePickerDate(formState.callEndAt, formState.callTimeZone)}
                      onChange={(date) => setGroupCallDateValue('callEndAt', date)}
                      placeholderText='mm/dd/yyyy h:mm aa'
                      css={[dateTimeInputClasses, errors.callEndAt && tw`ring-tRed focus:ring-tRed`]}
                      showIcon={true}
                      showTimeSelect={true}
                      timeIntervals={15}
                      dateFormat='MM/dd/yyyy h:mm aa'
                      icon={
                        <CgCalendar
                          className='!w-5 !h-5 top-1/2 -translate-y-1/2'
                          onClick={() => callEndRef?.current?.setOpen(true)}
                        />
                      }
                      popperProps={{ placement: 'bottom-start', strategy: 'fixed' }}
                      onKeyDown={(e) => {
                        if (e.code === 'Tab') {
                          e.preventDefault()
                          e.stopPropagation()
                          callEndRef?.current?.setOpen(false)
                          moveFocusOnKeyPress(e)
                        }
                      }}
                      onCalendarOpen={() => callStartRef?.current?.setOpen(false)}
                    />
                    {errors?.callEndAt && <p className='flex text-xs mt-1 text-tRed'>{errors.callEndAt.message}</p>}
                  </div>
                </div>
                <TimeZoneInput
                  inputRef={callTimeZoneRef}
                  value={formState.callTimeZone}
                  onChange={handleGroupCallTimeZoneChange}
                  clearErrors={clearErrors}
                  error={errors?.callTimeZone}
                  atTime={formState.callStartAt}
                />
              </>
            )}

            <div className='mb-4'>
              <label className='inline-flex font-semibold text-tBlack mb-1'>
                {hasButton ? 'Button photo' : 'Photo'}
              </label>
              <p className='text-xs text-gray-500 mb-2'>
                {hasButton ? 'Thumbnail photo on the button.' : 'Photo inside the post.'}
              </p>
              <div className='flex flex-col items-start'>
                <button
                  type='button'
                  onClick={() => fileInputRef.current?.click()}
                  className='relative w-44 aspect-video rounded-xl overflow-hidden bg-gray-100 hover:bg-gray-200 transition-colors'
                  aria-label={imagePreviewUrl ? 'Change photo' : 'Add photo'}
                >
                  {imagePreviewUrl && hasButton ? (
                    <FittedThumb src={imagePreviewUrl} alt='post' />
                  ) : imagePreviewUrl ? (
                    <img src={imagePreviewUrl} alt='post' className='w-full h-full object-cover' />
                  ) : (
                    <CgImage className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-gray-400 w-9 h-9' />
                  )}
                </button>
                <input ref={fileInputRef} type='file' accept='image/*' onChange={handlePickImage} className='hidden' />
                <div className='flex items-center mt-2'>
                  <button
                    type='button'
                    onClick={() => fileInputRef.current?.click()}
                    className='text-sm font-semibold text-tGreen hover:underline mr-4'
                  >
                    {imagePreviewUrl ? 'Change photo' : 'Add photo'}
                  </button>
                  {imagePreviewUrl && (
                    <button
                      type='button'
                      onClick={handleRemoveImage}
                      className='text-sm font-semibold text-tRed hover:underline inline-flex items-center'
                    >
                      <CgTrash className='w-4 h-4 mr-1' /> Remove
                    </button>
                  )}
                </div>
                <p className='text-xs text-gray-500 mt-1'>
                  {formState.buttonType === 'exercise' && exVidOrientation === 'portrait'
                    ? 'Use a 16:9 landscape or 9:16 portrait image'
                    : 'Use a 16:9 landscape image'}
                </p>
              </div>
            </div>

            <div className='flex flex-col space-y-4 border-t border-gray-200 pt-4'>
              {isNewPost && (
                <Switch
                  name='notifyMembers'
                  onChange={({ target: { checked } }) => setValue('notifyMembers', checked)}
                  label='Notify all members'
                  isChecked={formState.notifyMembers === true}
                  tooltipText={notifyMembersTooltip}
                />
              )}
              <Switch
                name='pinned'
                onChange={({ target: { checked } }) => setValue('pinned', checked)}
                label='Pin to top'
                isChecked={formState.pinned === true}
                tooltipText='Pinned posts show at the top of the feed for 30 days, or until you unpin them'
              />
              <Switch
                name='repliesOff'
                onChange={({ target: { checked } }) => setValue('repliesOff', checked)}
                label='Turn off replies'
                isChecked={formState.repliesOff === true}
                tooltipText='Members will not be able to reply to this post'
              />
            </div>
          </div>
        </form>
      </div>
      <UpdateActions
        itemKey={post?.id}
        handleSubmit={handleSubmit}
        loading={loading}
        actionText={isNewPost ? 'Post' : 'Save'}
        disabled={loading}
        form='communityPostForm'
        hideDelete={true}
        ref={submitRef}
      />
    </>
  )
}

const dateTimeInputClasses = tw`h-10 w-full rounded-lg bg-white border-0 ring-1 ring-gray-300 text-tBlack py-1.5 !pl-9
placeholder:text-tBlack placeholder:text-opacity-50 focus:border-0 focus:ring-tGreen focus-visible:outline-none
disabled:bg-gray-100 disabled:cursor-not-allowed`

// The next full hour in the given timezone, as a UTC timestamp.
function getDefaultCallStartAt(timeZone, now) {
  const startDate = new Date(now + ONE_HOUR_MS)
  startDate.setMinutes(0, 0, 0)

  try {
    return zonedDateTimeToUtcMs(startDate, timeZone)
  } catch (error) {
    return startDate.getTime()
  }
}
